import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class ProductBatch extends ModelObject<ProductBatch> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Unique code assigned to this ProductBatch, for printing on labels and entry / scanning.
    * Not Nullable
    * Not Empty
    */
    public code: string = "";
    /**
    * Stock Item which this is a batch of.
    */
    public stockItemId: Guid = Guid.createEmpty();
    /**
    * Urn
    * Not Nullable
    * Max Length : 1000
    */
    public urn: string = "";
    /**
    * Timestamp at which the product batch was created on the system. Captured by system not user editable.
    */
    public createdAt: Instant = Instant.ofEpochMilli(0);

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.code = jsonSerializedObject.code;
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.urn = jsonSerializedObject.urn;
          this.createdAt = Instant.parse(jsonSerializedObject.createdAt);
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.code = this.code;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.urn = this.urn;
        toRet.createdAt = this.createdAt.toString();
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "ProductBatch.Id cannot be empty"},
            {field: "code", rule: (v: any) => v == null || v.length !== 0 || "Code cannot be empty"},
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "ProductBatch.StockItemId cannot be empty"},
            {field: "urn", rule: (v: any) => v == null || v.length <= 1000 || "Urn has too many characters (1000)"},
    ];
}


export class ProductBatchStockItemIdUrn extends ModelObject<ProductBatchStockItemIdUrn> {

    /**
    * Stock Item which this is a batch of.
    */
    public stockItemId: Guid = Guid.createEmpty();
    /**
    * Urn
    * Not Nullable
    * Max Length : 1000
    */
    public urn: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.stockItemId = Guid.parseOrEmpty(jsonSerializedObject.stockItemId);
          this.urn = jsonSerializedObject.urn;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.stockItemId = this.stockItemId.toString();
        toRet.urn = this.urn;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "stockItemId", rule: (v: any) => !v.isEmpty() || "ProductBatchStockItemIdUrn.StockItemId cannot be empty"},
            {field: "urn", rule: (v: any) => v == null || v.length <= 1000 || "Urn has too many characters (1000)"},
    ];
}


export class ProductBatchFilter extends ModelObject<ProductBatchFilter> {

    private _maximumResults: number | null = null;
    public get maximumResults(): number | null { return this._maximumResults }
    public set maximumResults(value: number | null) {
      this._maximumResults = value === undefined ? null : value;
    }
    private _fromCreatedAt: Instant | null = null;
    /**
    * Minimum (inclusive) created at timestamp
    */
    public get fromCreatedAt(): Instant | null { return this._fromCreatedAt }
    public set fromCreatedAt(value: Instant | null) {
      this._fromCreatedAt = value === undefined ? null : value;
    }
    private _toCreatedAt: Instant | null = null;
    /**
    * Maximum (inclusive) created at timestamp
    */
    public get toCreatedAt(): Instant | null { return this._toCreatedAt }
    public set toCreatedAt(value: Instant | null) {
      this._toCreatedAt = value === undefined ? null : value;
    }
    private _codes: readonly (string)[] | null = null;
    public get codes(): readonly (string)[] | null { return this._codes }
    public set codes(value: readonly (string)[] | null) {
      this._codes = value === undefined ? null : value;
    }
    private _stockItemIds: readonly (Guid)[] | null = null;
    public get stockItemIds(): readonly (Guid)[] | null { return this._stockItemIds }
    public set stockItemIds(value: readonly (Guid)[] | null) {
      this._stockItemIds = value === undefined ? null : value;
    }
    private _productBatchIds: readonly (Guid)[] | null = null;
    public get productBatchIds(): readonly (Guid)[] | null { return this._productBatchIds }
    public set productBatchIds(value: readonly (Guid)[] | null) {
      this._productBatchIds = value === undefined ? null : value;
    }

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.maximumResults = jsonSerializedObject.maximumResults == null ? null : jsonSerializedObject.maximumResults;
          this.fromCreatedAt = jsonSerializedObject.fromCreatedAt == null ? null : Instant.parse(jsonSerializedObject.fromCreatedAt);
          this.toCreatedAt = jsonSerializedObject.toCreatedAt == null ? null : Instant.parse(jsonSerializedObject.toCreatedAt);
          this.codes = jsonSerializedObject.codes == null ? null : jsonSerializedObject.codes.map((v: any) => v);
          this.stockItemIds = jsonSerializedObject.stockItemIds == null ? null : jsonSerializedObject.stockItemIds.map((v: any) => Guid.parseOrEmpty(v));
          this.productBatchIds = jsonSerializedObject.productBatchIds == null ? null : jsonSerializedObject.productBatchIds.map((v: any) => Guid.parseOrEmpty(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.maximumResults = this.maximumResults == null ? null : this.maximumResults;
        toRet.fromCreatedAt = this.fromCreatedAt == null ? null : this.fromCreatedAt.toString();
        toRet.toCreatedAt = this.toCreatedAt == null ? null : this.toCreatedAt.toString();
        toRet.codes = this.codes == null ? null : this.codes.map(v => v);
        toRet.stockItemIds = this.stockItemIds == null ? null : this.stockItemIds.map(v => v.toString());
        toRet.productBatchIds = this.productBatchIds == null ? null : this.productBatchIds.map(v => v.toString());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "maximumResults", rule: (v: any) => v == null || v >= 0 || "MaximumResults is not greater than minimum allowed value: 0"},
            {field: "maximumResults", rule: (v: any) => v == null || v <= 2147483647 || "MaximumResults is above maximum allowed value: 2147483647"},
    ];
}


export class FilteredProductBatchesResult extends ModelObject<FilteredProductBatchesResult> {

    public matchingCount: number = 0;
    public productBatches: (ProductBatch)[] = [];

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.matchingCount = jsonSerializedObject.matchingCount;
          this.productBatches = jsonSerializedObject.productBatches.map((v: any) => new ProductBatch(v));
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.matchingCount = this.matchingCount;
        toRet.productBatches = this.productBatches.map(v => v.toJsonSerializedObject());
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "matchingCount", rule: (v: any) => v == null || v >= -2147483648 || "MatchingCount is not greater than minimum allowed value: -2147483648"},
            {field: "matchingCount", rule: (v: any) => v == null || v <= 2147483647 || "MatchingCount is above maximum allowed value: 2147483647"},
            {field: "productBatches", rule: (v: any) => !!v || "productBatches is required"},
            {field: "productBatches", rule: (v: any) => {
                if (v === null) {
                    return true;
                }

                return (v as ProductBatch[]).reduce((acc: Array<string>, curr: ProductBatch) => acc.concat(curr.validate()), new Array<string>());
            }},
    ];
}


export class ProductBatchDetails extends ModelObject<ProductBatchDetails> {

    /**
    * Unique code assigned to this ProductBatch, for printing on labels and entry / scanning.
    * Not Nullable
    * Not Empty
    */
    public code: string = "";
    /**
    * The Item Id of this product batch. Note that for product batches from affiliated tenants this will be the ItemId in the context Tenant,  not the ItemId in the originating tenant.
    */
    public itemId: Guid = Guid.createEmpty();
    /**
    * Urn
    * Not Nullable
    * Max Length : 1000
    */
    public urn: string = "";

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.code = jsonSerializedObject.code;
          this.itemId = Guid.parseOrEmpty(jsonSerializedObject.itemId);
          this.urn = jsonSerializedObject.urn;
        }

    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.code = this.code;
        toRet.itemId = this.itemId.toString();
        toRet.urn = this.urn;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "code", rule: (v: any) => v == null || v.length !== 0 || "Code cannot be empty"},
            {field: "itemId", rule: (v: any) => !v.isEmpty() || "ProductBatchDetails.ItemId cannot be empty"},
            {field: "urn", rule: (v: any) => v == null || v.length <= 1000 || "Urn has too many characters (1000)"},
    ];
}


export class ProductBatchService extends ServiceBase {

  public async getProductBatches(ids: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(ProductBatch)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    ids:ids.map(v => v.toString()),
    };
    const extractor = (response:any) => response.productBatches.map((v: any) => new ProductBatch(v));;
    return this.makeJsonRequest("productBatch/v1/getProductBatches", req, false, extractor, opts);
  };

  public async getProductBatchesByCodes(codes: readonly string[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(ProductBatch)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    codes:codes.map(v => v),
    };
    const extractor = (response:any) => response.productBatches.map((v: any) => new ProductBatch(v));;
    return this.makeJsonRequest("productBatch/v1/getProductBatchesByCodes", req, false, extractor, opts);
  };

  public async getOrCreateProductBatch(stockItemId: Guid, urn: string, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<ProductBatch> {
    const req = {
    tenantId:api.tenantId.toString(),
    stockItemId:stockItemId.toString(),
    urn:urn,
    };
    const extractor = (response:any) => new ProductBatch(response.productBatch);;
    return this.makeJsonRequest("productBatch/v1/getOrCreateProductBatch", req, false, extractor, opts);
  };

  public async getOrCreateProductBatches(items: readonly ProductBatchStockItemIdUrn[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(ProductBatch)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    items:items.map(v => v.toJsonSerializedObject()),
    };
    const extractor = (response:any) => response.productBatches.map((v: any) => new ProductBatch(v));;
    return this.makeJsonRequest("productBatch/v1/getOrCreateProductBatches", req, false, extractor, opts);
  };

  public async getProductBatchesByFilter(filter: ProductBatchFilter, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<FilteredProductBatchesResult> {
    const req = {
    tenantId:api.tenantId.toString(),
    filter:filter.toJsonSerializedObject(),
    };
    const extractor = (response:any) => new FilteredProductBatchesResult(response.results);;
    return this.makeJsonRequest("productBatch/v1/getProductBatchesByFilter", req, false, extractor, opts);
  };

  public async getProductBatchDetailsWithAffiliatedTenants(fromCreatedAt: Instant, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(ProductBatchDetails)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    fromCreatedAt:fromCreatedAt.toString(),
    };
    const extractor = (response:any) => response.results.map((v: any) => new ProductBatchDetails(v));;
    return this.makeJsonRequest("productBatch/v1/getProductBatchDetailsWithAffiliatedTenants", req, false, extractor, opts);
  };
}

export const productBatchService = new ProductBatchService();